import {
  Children,
  Fragment,
  ReactElement,
  ReactNode,
  isValidElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Select } from "antd";
import type { SelectProps } from "antd";
import { UIMenuItemProps } from "./MenuItem";

export type UISelectChangeEvent<T = unknown> = {
  preventDefault: () => void;
  target: {
    value: T;
  };
};

export type UISelectProps<T = unknown> = Omit<
  SelectProps<T, { label: ReactNode; value: T }>,
  "options" | "onChange"
> & {
  children?: ReactNode;
  options?: Array<{ value: T; label: ReactNode }>;
  onChange?: (event: UISelectChangeEvent<T>) => void;
};

export const UISelect = <T,>(props: UISelectProps<T>) => {
  const {
    children,
    options: optionsProp,
    onChange,
    defaultValue: defaultValueProp,
    value: valueProp,
    ...restProps
  } = props;
  const derivedOptions = useMemo(() => {
    const nextOptions: Array<{ value: T; label: ReactNode }> = [];
    const seenValues = new Set<string>();

    const appendOptions = (nodes: ReactNode) => {
      Children.forEach(nodes, (child) => {
        if (!isValidElement(child)) return;

        if (child.type === Fragment) {
          appendOptions((child as ReactElement<{ children?: ReactNode }>).props.children);
          return;
        }

        const propsWithValue = (child as ReactElement<Partial<UIMenuItemProps>>).props;
        if (!("value" in propsWithValue)) return;

        const value = propsWithValue.value as T;
        const dedupeKey = String(value);
        if (seenValues.has(dedupeKey)) return;
        seenValues.add(dedupeKey);

        nextOptions.push({
          value,
          label: propsWithValue.children,
        });
      });
    };

    appendOptions(children);
    return nextOptions;
  }, [children]);

  const options = optionsProp ?? derivedOptions;
  const isControlled = Object.prototype.hasOwnProperty.call(props, "value");

  const dataKey = (restProps as Record<string, unknown>)["data-key"] as
    | string
    | undefined;
  const propsRecord = restProps as Record<string, unknown>;
  const { ...selectProps } = propsRecord;
  delete (selectProps as Record<string, unknown>)["data-key"];
  const defaultValue = defaultValueProp as T | undefined;
  const controlledValue = valueProp as T | undefined;

  const [uncontrolledValue, setUncontrolledValue] = useState<T | undefined>(
    () => defaultValue
  );

  const resolvedValue =
    isControlled ? controlledValue : uncontrolledValue;

  const hiddenSelectValue = useMemo(() => {
    if (resolvedValue === undefined || resolvedValue === null) return "";
    return resolvedValue as string | number;
  }, [resolvedValue]);

  return (
    <>
      <Select
        options={options}
        value={resolvedValue as any}
        onChange={(value) => {
          if (!isControlled) {
            setUncontrolledValue(value as T);
          }
          onChange?.({
            preventDefault: () => undefined,
            target: { value: value as T },
          });
        }}
        {...(selectProps as any)}
      />
      {dataKey ? (
        <input
          type="hidden"
          data-key={dataKey}
          value={hiddenSelectValue}
          readOnly
          aria-hidden="true"
        />
      ) : null}
    </>
  );
};
